import type { Platform } from './types';

/**
 * "Results" is the headline outcome metric — but it means a different event on
 * each platform: Google counts attributed conversions, Meta counts leads from
 * Pixel/CAPI. They are NOT the same event and must never be summed into one
 * figure (the "no false-equivalence" rule of the consolidated surface, #5972).
 */
export type ResultKind = 'conversions' | 'leads';

export const RESULT_KIND: Record<Platform, ResultKind> = {
  google: 'conversions',
  meta: 'leads',
};

export interface PlatformResult {
  platform: Platform;
  kind: ResultKind;
  value: number;
}

/**
 * Build a PlatformResult. Prefer this over a bare number so the kind
 * (conversions vs leads) always travels with the value.
 */
export function platformResult(platform: Platform, value: number): PlatformResult {
  return { platform, kind: RESULT_KIND[platform], value };
}

/**
 * Sum results ONLY within a single kind (e.g. total Google conversions across
 * campaigns). Throws on mixed kinds — summing conversions and leads is a
 * category error, so it fails loudly rather than inventing a merged number.
 * Returns null for an empty list.
 */
export function sumSameKind(
  results: PlatformResult[],
): { kind: ResultKind; value: number } | null {
  if (results.length === 0) return null;
  const kind = results[0].kind;
  if (results.some((r) => r.kind !== kind)) {
    throw new Error('Results of different kinds (conversions vs leads) cannot be summed');
  }
  return { kind, value: results.reduce((sum, r) => sum + r.value, 0) };
}

/**
 * Render results split by kind, e.g. "612 conversions · 289 leads". Zero-value
 * kinds are hidden while another kind has results; an all-zero set prints its
 * measured zeros with units (a recorded zero is data — the sums rule; dashes
 * are reserved for undefined ratios, 09-08). Only an EMPTY set (no platforms
 * reporting at all) renders as "—".
 */
export function formatResultsSplit(results: PlatformResult[]): string {
  const shown = results.filter((r) => r.value > 0);
  if (shown.length === 0) {
    if (results.length === 0) return '—';
    return results.map((r) => `0 ${r.kind}`).join(' · ');
  }
  return shown.map((r) => `${Math.round(r.value).toLocaleString()} ${r.kind}`).join(' · ');
}
